import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import QRCode from "react-qr-code";
import { FiTruck, FiMapPin, FiCreditCard } from "react-icons/fi";

const Transport = () => {
  const router = useLocation();
  const enrollmentNo = router.state?.loginid;

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRoute, setSavingRoute] = useState("");
  const [transportChoice, setTransportChoice] = useState(null);
  const [stopSelections, setStopSelections] = useState({});
  const [paymentRefs, setPaymentRefs] = useState({});
  const [seatMap, setSeatMap] = useState(null);
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [showAttendanceHistory, setShowAttendanceHistory] = useState(false);

  const headers = useMemo(() => ({ "Content-Type": "application/json" }), []);

  useEffect(() => {
    if (!enrollmentNo) {
      setLoading(false);
      return;
    }
    fetchRoutes();
    fetchStudentChoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentNo]);

  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${baseApiURL()}/transport/routes`, { headers });
      if (res.data.success) {
        const filteredRoutes = res.data.routes.map(route => ({
          ...route,
          capacity: undefined,
          allocatedSeats: undefined,
          seatConfig: undefined
        }));
        setRoutes(filteredRoutes || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Unable to load buses.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentChoice = async () => {
    try {
      const res = await axios.get(`${baseApiURL()}/transport/student/${enrollmentNo}`, { headers });
      if (res.data.success && res.data.transport) {
        setTransportChoice(res.data.transport);
        if (res.data.transport.routeId && res.data.transport.seatNumber) {
          fetchSeatMap(res.data.transport.routeId);
        }
        if (res.data.transport.status === "active") {
          fetchAttendanceHistory();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSeatMap = async (routeId) => {
    try {
      const res = await axios.get(`${baseApiURL()}/transport/routes/${routeId}/seatmap`);
      if (res.data.success) {
        const filteredSeatMap = {
          ...res.data,
          seatConfig: undefined,
          statistics: {
            occupiedSeats: res.data.statistics.occupiedSeats,
          }
        };
        setSeatMap(filteredSeatMap);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const res = await axios.get(`${baseApiURL()}/transport/attendance/history/${enrollmentNo}`, { headers });
      if (res.data.success) {
        setAttendanceHistory(res.data.attendanceHistory || []);
      }
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    }
  };

  const generateStudentQR = async () => {
    if (!enrollmentNo) {
      toast.error("Session expired. Login again.");
      return;
    }

    try {
      const res = await axios.get(`${baseApiURL()}/transport/attendance/qr/${enrollmentNo}`, { headers });
      if (res.data.success) {
        setQrData(res.data);
        setShowQR(true);
        toast.success("QR code generated successfully!");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error("QR generation error:", error);
      toast.error("Unable to generate QR code");
    }
  };

  const downloadQR = () => {
    if (!qrData) return;
    
    const svg = document.getElementById("qr-code-svg");
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `transport-pass-${enrollmentNo}-${Date.now()}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
        toast.success("QR code downloaded!");
      };
      
      img.src = "data:image/svg+xml;base64," + btoa(svgData);
    }
  };

  const handleStopChange = (routeId, stopName) => {
    setStopSelections((prev) => ({ ...prev, [routeId]: stopName }));
  };

  const handlePaymentRefChange = (routeId, value) => {
    setPaymentRefs((prev) => ({ ...prev, [routeId]: value }));
  };

  const handleEnroll = async (route) => {
    if (!enrollmentNo) {
      toast.error("Session expired. Login again.");
      return;
    }
    const selectedStop = stopSelections[route._id];
    if (!selectedStop) {
      toast.error("Select a stop/station first.");
      return;
    }

    try {
      setSavingRoute(route._id);
      const payload = {
        enrollmentNo,
        routeId: route._id,
        stopName: selectedStop,
        paymentReference: paymentRefs[route._id],
      };
      const res = await axios.post(`${baseApiURL()}/transport/enroll`, payload, { headers });
      if (res.data.success) {
        toast.success("Transport preference saved.");
        setTransportChoice(res.data.transport);
        if (res.data.transport.routeId) {
          fetchSeatMap(res.data.transport.routeId);
        }
        fetchAttendanceHistory();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to save preference.");
    } finally {
      setSavingRoute("");
    }
  };

  // Function to render the seat map
  const renderSeatMap = () => {
    if (!seatMap || !transportChoice?.seatNumber) return null;

    const studentSeat = seatMap.seatMap
      .flatMap(row => row.seats)
      .find(seat => seat.seatNumber === transportChoice.seatNumber);

    return (
      <div className="mt-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Your Seat Details</h3>
          <button
            onClick={() => setShowSeatMap(!showSeatMap)}
            className="px-3.5 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-500 cursor-pointer"
          >
            {showSeatMap ? "Hide Seat Map" : "Show Seat Map"}
          </button>
        </div>

        {/* Seat Information Card */}
        <div className="bg-indigo-50 dark:bg-slate-950 border border-indigo-100 dark:border-slate-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-3xl">💺</div>
                <div>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold uppercase">Your Assigned Seat</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">{transportChoice.seatNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 bg-indigo-500 rounded-full"></span>
                  Seat Type: <span className="capitalize">{transportChoice.seatType || 'window'}</span>
                </span>
                {studentSeat && (
                  <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                    <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></span>
                    Section: <span className="capitalize">{studentSeat.section}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 dark:text-slate-400">Seat Location</p>
              {studentSeat && (
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Row {studentSeat.row}, Position {studentSeat.position}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bus Seat Map */}
        {showSeatMap && seatMap && (
          <div className="space-y-6">
            <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
              {/* Driver Area */}
              <div className="flex justify-center mb-6">
                <div className="bg-slate-200 dark:bg-slate-800 px-6 py-2 rounded-xl text-slate-800 dark:text-slate-200">
                  <span className="text-xs font-bold">🚌 Driver & Staff Area</span>
                </div>
              </div>

              {/* Seats Grid */}
              <div className="space-y-4">
                {seatMap.seatMap.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex items-center gap-4">
                    <div className="w-16 text-xs font-bold text-slate-500 dark:text-slate-400">
                      Row {row.rowNumber}
                    </div>
                    <div className="flex-1 flex items-center justify-center gap-3">
                      {row.seats.map((seat, seatIndex) => (
                        <div
                          key={seatIndex}
                          className={`
                            w-14 h-14 rounded-xl flex flex-col items-center justify-center
                            font-bold text-xs relative border
                            ${seat.isOccupied && seat.seatNumber === transportChoice.seatNumber
                              ? 'bg-indigo-50 dark:bg-indigo-950/80 border-indigo-500 text-indigo-700 dark:text-indigo-300 shadow-md'
                              : seat.isOccupied
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                              : seat.isReserved
                              ? 'bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-500'
                              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }
                          `}
                        >
                          <div>{seat.seatNumber}</div>
                          {seat.isOccupied && (
                            <div className="text-[10px] mt-0.5 truncate max-w-full px-1">
                              {seat.allocation?.enrollmentNo === enrollmentNo ? "YOU" : "Occupied"}
                            </div>
                          )}
                          
                          {seat.seatNumber === transportChoice.seatNumber && (
                            <div className="absolute -top-1.5 -right-1.5">
                              <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center shadow-xs">
                                <span className="text-[10px] text-white font-bold">★</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render QR Code Modal
  const renderQRCode = () => {
    if (!qrData || !showQR) return null;

    return (
      <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Your Transport Pass QR</h3>
            <button
              onClick={() => setShowQR(false)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl cursor-pointer"
            >
              &times;
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-center p-4 bg-white rounded-xl border border-slate-200">
              <QRCode
                id="qr-code-svg"
                value={qrData.qrString}
                size={220}
                level="H"
                fgColor="#4f46e5"
                bgColor="#ffffff"
              />
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
              <h4 className="font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider mb-2">Student Details</h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Name:</span>
                  <p className="font-bold text-slate-900 dark:text-white">{qrData.student.name}</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Enrollment:</span>
                  <p className="font-bold text-slate-900 dark:text-white font-mono">{qrData.student.enrollmentNo}</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Bus:</span>
                  <p className="font-bold text-slate-900 dark:text-white">{qrData.student.busNumber}</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Seat:</span>
                  <p className="font-bold text-slate-900 dark:text-white">{qrData.student.seatNumber || "Not assigned"}</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-2">
              <button
                onClick={downloadQR}
                className="flex-1 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-500/10 font-bold text-xs cursor-pointer"
              >
                📥 Download QR
              </button>
              <button
                onClick={generateStudentQR}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 font-bold text-xs cursor-pointer shadow-md shadow-indigo-600/30"
              >
                🔄 Refresh QR
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full space-y-6">
      {/* 🌟 Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2 text-slate-900 dark:text-white">
            <FiTruck className="text-indigo-600 dark:text-indigo-400" />
            <span>College Transport</span>
          </h1>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">Explore available buses, review stops, and confirm your seat</p>
        </div>
      </div>

      {transportChoice && (
        <div className="bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800 rounded-2xl shadow-xs p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 flex items-center justify-center text-3xl">
                  🚌
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-bold">
                    My Active Pass
                  </p>
                  <h2 className="text-xl font-black text-slate-900 dark:text-white mt-0.5">
                    {transportChoice.routeName} • {transportChoice.busNumber}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Boarding at <strong className="text-slate-900 dark:text-white">{transportChoice.stopName}</strong>
                  </p>
                </div>
              </div>
              
              {transportChoice.seatNumber && (
                <div className="bg-indigo-50 dark:bg-slate-950 border border-indigo-100 dark:border-slate-800 rounded-xl p-3.5 inline-block">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">💺</div>
                    <div>
                      <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold uppercase">Assigned Seat</p>
                      <p className="text-xl font-black text-slate-900 dark:text-white">{transportChoice.seatNumber}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="text-right space-y-3">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Paid Amount</p>
                <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">₹{transportChoice.fare}</p>
              </div>
              
              <div className="flex flex-wrap gap-2 justify-end">
                {transportChoice.seatNumber && (
                  <button
                    onClick={() => setShowSeatMap(!showSeatMap)}
                    className="px-3.5 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-500 cursor-pointer shadow-xs"
                  >
                    {showSeatMap ? "Hide Seat Map" : "View Seat Map"}
                  </button>
                )}
                
                <button
                  onClick={generateStudentQR}
                  className="px-3.5 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-500 cursor-pointer shadow-xs"
                >
                  📱 Show QR Pass
                </button>
              </div>
            </div>
          </div>
          
          {transportChoice.seatNumber && renderSeatMap()}
        </div>
      )}

      {/* Routes List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
          </div>
        ) : !routes.length ? (
          <div className="text-center py-12 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 dark:text-slate-400 text-xs font-medium">
            Transport routes are not published yet. Check back later.
          </div>
        ) : (
          routes.map((route) => {
            const selectedStop = route.stops.find(
              (stop) => stop.name === stopSelections[route._id]
            );
            
            const studentAllocation = route.allocations?.find(
              alloc => alloc.enrollmentNo === enrollmentNo
            );
            const hasSeatAssignment = studentAllocation?.seatNumber;

            return (
              <div key={route._id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {route.routeName} • {route.busNumber}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{route.busName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Choose Stop / Station</label>
                    <select
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={stopSelections[route._id] || ""}
                      onChange={(e) => handleStopChange(route._id, e.target.value)}
                      disabled={hasSeatAssignment}
                    >
                      <option value="">Select stop</option>
                      {route.stops.map((stop) => (
                        <option key={stop.code || stop.name} value={stop.name}>
                          {stop.name}
                          {typeof stop.distanceKm === "number" && ` • ${stop.distanceKm} km`}
                          {typeof stop.fare === "number" && ` • ₹${stop.fare}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Payment Reference</label>
                    <input
                      type="text"
                      placeholder="Txn / UPI Ref (optional)"
                      value={paymentRefs[route._id] || ""}
                      onChange={(e) => handlePaymentRefChange(route._id, e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={hasSeatAssignment}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Estimated Fare</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white">
                      ₹{typeof selectedStop?.fare === "number" ? selectedStop.fare : route.baseFare}
                    </p>
                  </div>
                  <button
                    onClick={() => handleEnroll(route)}
                    disabled={savingRoute === route._id || hasSeatAssignment}
                    className={`px-5 py-2.5 rounded-xl font-bold text-xs shadow-xs transition cursor-pointer ${
                      hasSeatAssignment
                        ? 'bg-emerald-600 text-white cursor-default'
                        : savingRoute === route._id
                        ? 'bg-slate-400 text-white cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-500'
                    }`}
                  >
                    {hasSeatAssignment ? `Seat ${studentAllocation.seatNumber} Assigned` : "Select & Pay"}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {renderQRCode()}
    </div>
  );
};

export default Transport;